import { useMemo, useState } from "react";
import { ExternalLink, Loader2, X } from "lucide-react";
import { isValidWallet } from "@hupper/shared";
import {
  formatUSDT,
  parseUSDT,
  useUSDTBalance,
  useVaultActions,
  useVaultBalance,
} from "../hooks/useVault";
import { ACTIVE_CHAIN } from "../contracts/chains";

interface Props {
  walletAddress: string | null;
  onClose: () => void;
}

/**
 * WithdrawModal — pulls USDT OUT OF the Hupper vault.
 *
 * Two destinations supported:
 *   * "to my wallet"  — vault.withdraw(amount). One tx. Lands back in
 *     the connected wallet's ERC20 balance. Default path.
 *   * "to an external address" — vault.withdraw(amount) THEN
 *     usdt.transfer(destination, amount). Two txs, two wallet
 *     confirmations. Used for off-ramping to Binance / Coinbase / hardware.
 *
 * Replaces the previous version which called usdt.transfer directly
 * from the wallet, never touching the vault — that broke the
 * Polymarket-style mental model ("balance" = playable = vault custody).
 */
export default function WithdrawModal({ walletAddress, onClose }: Props) {
  const [mode, setMode] = useState<"self" | "external">("self");
  const [destination, setDestination] = useState("");
  const [amount, setAmount] = useState("");
  const [stage, setStage] = useState<"idle" | "withdrawing" | "transferring" | "success">("idle");

  const usdt = useUSDTBalance(walletAddress, 15_000);
  const vault = useVaultBalance(walletAddress, 15_000);
  const { withdraw, sendUSDT, status } = useVaultActions();

  const destinationValid = useMemo(() => {
    if (mode === "self") return true;
    if (!destination.trim()) return null;
    return isValidWallet(destination.trim());
  }, [destination, mode]);

  const parsedAmount = useMemo(() => {
    if (!amount.trim()) return null;
    try { return parseUSDT(amount); } catch { return null; }
  }, [amount]);

  const exceedsBalance = parsedAmount !== null && parsedAmount > vault.value;

  const submitDisabled =
    parsedAmount === null ||
    parsedAmount === 0n ||
    exceedsBalance ||
    (mode === "external" && destinationValid !== true) ||
    stage !== "idle" ||
    status.pending;

  const submitLabel = (() => {
    if (stage === "withdrawing") return "Withdrawing from vault…";
    if (stage === "transferring") return "Sending to address…";
    if (stage === "success") return "Done ✓";
    return mode === "self" ? "Withdraw to my wallet" : "Withdraw + send";
  })();

  const handleSubmit = async () => {
    if (parsedAmount === null || parsedAmount === 0n) return;
    try {
      // Step 1 — always: pull from vault into connected wallet.
      setStage("withdrawing");
      await withdraw(parsedAmount);
      // Step 2 — only when external: forward to destination address.
      if (mode === "external" && destinationValid === true) {
        setStage("transferring");
        await sendUSDT(destination.trim(), parsedAmount);
      }
      setStage("success");
      await Promise.all([usdt.refetch(), vault.refetch()]);
      setTimeout(() => {
        setStage("idle");
        setAmount("");
        setDestination("");
        onClose();
      }, 1500);
    } catch {
      setStage("idle");
    }
  };

  const explorerTx =
    status.txHash && ACTIVE_CHAIN.explorerUrl
      ? `${ACTIVE_CHAIN.explorerUrl}/tx/${status.txHash}`
      : null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-[#FF6B00] to-[#F54D12]" />

        <div className="p-5 flex items-center justify-between border-b border-white/5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-white/40 font-bold">
              {ACTIVE_CHAIN.name} · USDT
            </div>
            <div className="text-base font-semibold text-white">Withdraw from Hupper</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Destination toggle */}
          <div className="flex gap-2 p-1 bg-white/5 border border-white/10 rounded-lg">
            <button
              onClick={() => setMode("self")}
              disabled={stage !== "idle"}
              className={`flex-1 py-2 rounded-md text-[12px] font-semibold tracking-tight transition-colors ${
                mode === "self" ? "bg-white/10 text-white" : "text-white/40 hover:text-white"
              }`}
            >
              To my wallet
            </button>
            <button
              onClick={() => setMode("external")}
              disabled={stage !== "idle"}
              className={`flex-1 py-2 rounded-md text-[12px] font-semibold tracking-tight transition-colors ${
                mode === "external" ? "bg-white/10 text-white" : "text-white/40 hover:text-white"
              }`}
            >
              To external address
            </button>
          </div>

          {/* External destination input */}
          {mode === "external" && (
            <div>
              <label htmlFor="wd-dest" className="text-[11px] uppercase tracking-[0.14em] text-white/40 font-bold mb-1.5 block">
                Destination address
              </label>
              <input
                id="wd-dest"
                type="text"
                spellCheck={false}
                autoComplete="off"
                placeholder="0x… (Binance deposit address, hardware wallet, etc.)"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                disabled={stage !== "idle"}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 font-mono text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-[#FF6B00]/50 disabled:opacity-50"
              />
              {destinationValid === false && (
                <div className="mt-1.5 text-[11px] text-red-400 font-semibold">Invalid address.</div>
              )}
            </div>
          )}

          {/* Amount */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="wd-amount" className="text-[11px] uppercase tracking-[0.14em] text-white/40 font-bold">
                Amount
              </label>
              <button
                onClick={() => setAmount(formatUSDT(vault.value))}
                disabled={vault.value === 0n || stage !== "idle"}
                className="text-[11px] uppercase tracking-[0.14em] text-[#FF6B00] hover:text-[#F54D12] font-bold disabled:opacity-30"
              >
                Max: ${formatUSDT(vault.value)}
              </button>
            </div>
            <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 focus-within:border-[#FF6B00]/50">
              <input
                id="wd-amount"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                disabled={stage !== "idle"}
                className="flex-1 bg-transparent border-0 outline-none text-2xl font-bold tabular-nums py-3 text-white placeholder-white/20 disabled:opacity-50"
              />
              <span className="text-sm text-white/40 font-semibold">USDT</span>
            </div>
            {exceedsBalance && (
              <div className="mt-1.5 text-[11px] text-red-400 font-semibold">Exceeds Hupper balance.</div>
            )}
          </div>

          {mode === "external" && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-[12px] text-amber-200/90 leading-relaxed">
              External withdrawals run two transactions (vault → wallet, then wallet → address). Double-check the destination — sends on {ACTIVE_CHAIN.name} are irreversible.
            </div>
          )}

          {/* Status feedback */}
          {(stage !== "idle" || status.error) && (
            <div
              className={`rounded-lg p-3 text-[12px] flex items-start gap-2 ${
                status.error
                  ? "bg-red-500/10 border border-red-500/30 text-red-200"
                  : stage === "success"
                  ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200"
                  : "bg-white/5 border border-white/10 text-white/70"
              }`}
            >
              {(stage === "withdrawing" || stage === "transferring") && (
                <Loader2 className="w-4 h-4 mt-0.5 animate-spin shrink-0" />
              )}
              <div className="flex-1 leading-relaxed">
                {status.error ? (
                  <span>{status.error.message}</span>
                ) : stage === "withdrawing" ? (
                  mode === "external" ? "Step 1/2 — pulling USDT from your Hupper balance…" : "Pulling USDT from your Hupper balance…"
                ) : stage === "transferring" ? (
                  "Step 2/2 — sending USDT to destination…"
                ) : stage === "success" ? (
                  "Withdrawal confirmed."
                ) : null}
                {explorerTx && (
                  <a
                    href={explorerTx}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 ml-2 underline opacity-70 hover:opacity-100"
                  >
                    View tx <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="w-full py-3 rounded-lg bg-[#FF6B00] hover:bg-[#F54D12] disabled:opacity-30 disabled:cursor-not-allowed text-black font-bold text-sm tracking-tight transition-colors"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
